import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export const ParcelCard = ({ parcel, index = 0 }) => {
  const { addItem } = useCart();
  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({ id: parcel.id, name: parcel.name, price: parcel.price, image: parcel.image, type: "parcel" });
    toast.success(`${parcel.name} added to your cart 💝`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <Link
        to={`/parcel/${parcel.slug}`}
        data-testid={`parcel-card-${parcel.slug}`}
        className="group block overflow-hidden rounded-wp-lg bg-card border border-border shadow-wp-card transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-wp-lift"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-wp-cream-100">
          <img
            src={parcel.image}
            alt={parcel.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          />
          {parcel.badge && (
            <Badge className="absolute left-3 top-3 bg-wp-rose-strong text-white border-0 shadow">{parcel.badge}</Badge>
          )}
          <button
            onClick={onAdd}
            aria-label={`Add ${parcel.name} to cart`}
            data-testid={`parcel-quick-add-${parcel.slug}`}
            className="absolute bottom-3 right-3 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-wp-rose-strong shadow-wp-soft backdrop-blur transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-white active:scale-95"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-1 text-xs text-wp-ink-muted">
            <Star className="h-3.5 w-3.5 fill-wp-rose text-wp-rose" />
            <span className="price-num">{parcel.rating}</span>
            <span>· {parcel.includes?.length || 0} items inside</span>
          </div>
          <h3 className="mt-1 text-lg font-semibold leading-tight">{parcel.name}</h3>
          <p className="mt-0.5 line-clamp-1 text-sm text-wp-ink-muted">{parcel.tagline}</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="price-num text-lg font-semibold text-wp-rose-strong">{formatINR(parcel.price)}</span>
            {parcel.compare_at && (
              <span className="price-num text-sm text-wp-ink-muted line-through">{formatINR(parcel.compare_at)}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
